export type Lang = "en" | "es" | "pt";

export const LANGS: Lang[] = ["en", "es", "pt"];
export const DEFAULT_LANG: Lang = "en";

export const LANG_LABEL: Record<Lang, string> = {
  en: "EN",
  es: "ES",
  pt: "PT",
};

export const LANG_FULL: Record<Lang, string> = {
  en: "English",
  es: "Español",
  pt: "Português",
};

// Flat key dictionary. Use dot notation only for grouping clarity.
export const TRANSLATIONS = {
  // ───────── Header ─────────
  "nav.howItWorks": {
    en: "How It Works",
    es: "Cómo Funciona",
    pt: "Como Funciona",
  },
  "nav.whatWeSource": {
    en: "What We Source",
    es: "Qué Suministramos",
    pt: "O Que Fornecemos",
  },
  "nav.about": {
    en: "About",
    es: "Sobre Nosotros",
    pt: "Sobre Nós",
  },
  "nav.insights": {
    en: "Insights",
    es: "Análisis",
    pt: "Insights",
  },
  "nav.solutions": {
    en: "Solutions",
    es: "Soluciones",
    pt: "Soluções",
  },
  "nav.kits": {
    en: "Pre-Configured Power Packages",
    es: "Paquetes de Energía Pre-Configurados",
    pt: "Pacotes de Energia Pré-Configurados",
  },
  "nav.engineeredSolutions": {
    en: "Power Distribution Solutions",
    es: "Soluciones de Distribución Eléctrica",
    pt: "Soluções de Distribuição Elétrica",
  },
  "nav.industries": {
    en: "Industries",
    es: "Industrias",
    pt: "Indústrias",
  },
  "nav.caseStudies": {
    en: "Case Studies",
    es: "Casos de Éxito",
    pt: "Casos de Sucesso",
  },
  "nav.engineered": {
    en: "Power Distribution Solutions",
    es: "Soluciones de Distribución Eléctrica",
    pt: "Soluções de Distribuição Elétrica",
  },
  "nav.fastAccess": {
    en: "Fast Access Power Components",
    es: "Componentes Eléctricos de Acceso Rápido",
    pt: "Componentes Elétricos de Acesso Rápido",
  },
  "nav.fastAccess.desc": {
    en: "High-demand components, sourced and delivered quickly",
    es: "Componentes de alta demanda, suministrados y entregados rápidamente",
    pt: "Componentes de alta demanda, fornecidos e entregues rapidamente",
  },
  "nav.kits.desc": {
    en: "Standardized, ready-to-deploy power systems",
    es: "Sistemas eléctricos estandarizados y listos para implementar",
    pt: "Sistemas elétricos padronizados e prontos para implantar",
  },
  "nav.engineered.desc": {
    en: "Engineered systems for complex requirements",
    es: "Sistemas de ingeniería para requerimientos complejos",
    pt: "Sistemas de engenharia para requisitos complexos",
  },
  "nav.getQuote": { en: "Get Pricing", es: "Obtener Precio", pt: "Obter Preço" },

  // ───────── CTAs ─────────
  "cta.requestQuote": {
    en: "Get Pricing & Availability",
    es: "Obtener Precio y Disponibilidad",
    pt: "Obter Preço e Disponibilidade",
  },
  "cta.sendWhatsapp": {
    en: "Send RFQ via WhatsApp",
    es: "Enviar RFQ por WhatsApp",
    pt: "Enviar RFQ pelo WhatsApp",
  },
  "cta.getQuoteHours": {
    en: "Get Pricing & Availability",
    es: "Obtener Precio y Disponibilidad",
    pt: "Obter Preço e Disponibilidade",
  },
  "cta.chatWhatsapp": {
    en: "Chat on WhatsApp",
    es: "Chatear por WhatsApp",
    pt: "Conversar no WhatsApp",
  },
  "cta.responseExpectation": {
    en: "Get pricing, availability, and lead times within hours",
    es: "Recibe precios, disponibilidad y tiempos de entrega en pocas horas",
    pt: "Receba preços, disponibilidade e prazos de entrega em poucas horas",
  },
  "hero.proofLine": {
    en: "Submit SKU, link, or product details — receive structured pricing and delivery.",
    es: "Envía SKU, enlace o detalles del producto — recibe precio estructurado y entrega.",
    pt: "Envie SKU, link ou detalhes do produto — receba preço estruturado e entrega.",
  },
  "hero.microNoAccount": {
    en: "No account required",
    es: "Sin necesidad de cuenta",
    pt: "Sem necessidade de conta",
  },
  "hero.microResponse": {
    en: "Response in minutes",
    es: "Respuesta en minutos",
    pt: "Resposta em minutos",
  },
  "cta.activityProof": {
    en: "Recent requests from: Bogotá, Mexico City",
    es: "Solicitudes recientes desde: Bogotá, Ciudad de México",
    pt: "Solicitações recentes de: Bogotá, Cidade do México",
  },
  "cta.whatHappensNext": {
    en: "We review your request and send pricing + availability within a few hours",
    es: "Revisamos tu solicitud y enviamos precio y disponibilidad en pocas horas",
    pt: "Analisamos sua solicitação e enviamos preço e disponibilidade em poucas horas",
  },

  // ───────── Hero ─────────
  "hero.badge": {
    en: "U.S. Electrical Supply → LATAM · Quotes in <4 hours",
    es: "Suministro Eléctrico de EE. UU. → LATAM · Cotizaciones en <4 horas",
    pt: "Suprimento Elétrico dos EUA → LATAM · Cotações em <4 horas",
  },
  "hero.h1.part1": {
    en: "U.S. switchgear and electrical components,",
    es: "Switchgear y componentes eléctricos de EE. UU.,",
    pt: "Switchgear e componentes elétricos dos EUA,",
  },
  "hero.h1.part2": {
    en: "priced and delivered across LATAM",
    es: "cotizados y entregados en toda LATAM",
    pt: "cotados e entregues em toda a LATAM",
  },
  "hero.subtitle": {
    en: "Source U.S. electrical products with instant pricing, real availability, and full landed cost.",
    es: "Abastece productos eléctricos de EE. UU. con precio instantáneo, disponibilidad real y costo total entregado.",
    pt: "Adquira produtos elétricos dos EUA com preço instantâneo, disponibilidade real e custo total entregue.",
  },
  "hero.systemLine": {
    en: "Quotes in <4 hours. Landed (DDP) pricing upfront. Built for LATAM procurement.",
    es: "Cotizaciones en <4 horas. Precio DDP por adelantado. Hecho para compras en LATAM.",
    pt: "Cotações em <4 horas. Preço DDP antecipado. Feito para compras na LATAM.",
  },
  "hero.systemLine.label": {
    en: "Built for",
    es: "Hecho para",
    pt: "Feito para",
  },
  "hero.systemLine.desktop": {
    en: "Electrical contractors, industrial maintenance teams, and procurement teams sourcing U.S. components",
    es: "Contratistas eléctricos, equipos de mantenimiento industrial y equipos de compras que adquieren componentes de EE. UU.",
    pt: "Contratantes elétricos, equipes de manutenção industrial e equipes de compras que adquirem componentes dos EUA",
  },
  "hero.subtitle.mobile": {
    en: "Instant pricing, real availability, and full landed cost — across U.S. suppliers.",
    es: "Precio instantáneo, disponibilidad real y costo total entregado — entre proveedores de EE. UU.",
    pt: "Preço instantâneo, disponibilidade real e custo total entregue — entre fornecedores dos EUA.",
  },

  // ───────── Infrastructure Proof Strip ─────────
  "infraStrip.label": {
    en: "Infrastructure layer",
    es: "Capa de infraestructura",
    pt: "Camada de infraestrutura",
  },
  "infraStrip.1": {
    en: "Operating across LATAM",
    es: "Operando en toda LATAM",
    pt: "Operando em toda a LATAM",
  },
  "infraStrip.2": {
    en: "Global supplier network",
    es: "Red global de proveedores",
    pt: "Rede global de fornecedores",
  },
  "infraStrip.3": {
    en: "Structured quotes with verified delivery",
    es: "Cotizaciones estructuradas con entrega verificada",
    pt: "Cotações estruturadas com entrega verificada",
  },

  // ───────── Scale Signals (verified) ─────────
  "scaleStrip.label": {
    en: "Scale & reach",
    es: "Escala y alcance",
    pt: "Escala e alcance",
  },
  "scaleStrip.1": {
    en: "Serving projects across LATAM",
    es: "Atendiendo proyectos en toda LATAM",
    pt: "Atendendo projetos em toda a LATAM",
  },
  "scaleStrip.2": {
    en: "Sourcing from a verified U.S. supplier network",
    es: "Abastecimiento desde una red verificada de proveedores en EE. UU.",
    pt: "Sourcing a partir de uma rede verificada de fornecedores nos EUA",
  },
  "scaleStrip.3": {
    en: "Quote turnaround typically within hours",
    es: "Cotizaciones típicamente en pocas horas",
    pt: "Cotações tipicamente em poucas horas",
  },

  // ───────── Category Statement (single-placement positioning) ─────────
  "categoryStatement.aria": {
    en: "Category positioning",
    es: "Posicionamiento de categoría",
    pt: "Posicionamento de categoria",
  },
  "categoryStatement.line": {
    en: "Not a distributor. Not a marketplace. The procurement layer for LATAM's industrial buildout.",
    es: "No somos un distribuidor. No somos un marketplace. La capa de compras para el desarrollo industrial de LATAM.",
    pt: "Não somos uma distribuidora. Não somos um marketplace. A camada de compras para o desenvolvimento industrial da LATAM.",
  },

  // ───────── Thesis (Why this exists) ─────────
  "thesis.label": {
    en: "Why this exists",
    es: "Por qué existimos",
    pt: "Por que existimos",
  },
  "thesis.headline": {
    en: "Procurement across LATAM is fragmented — and slowing down critical projects.",
    es: "Las compras en LATAM están fragmentadas — y están retrasando proyectos críticos.",
    pt: "As compras na LATAM estão fragmentadas — e atrasando projetos críticos.",
  },
  "thesis.body": {
    en: "Switzh replaces fragmented supplier sourcing with a single request that aggregates supply, calculates full landed pricing (DDP), and executes procurement in one system. Switzh acts as the transaction layer for cross-border industrial procurement.",
    es: "Switzh reemplaza el sourcing fragmentado de proveedores con una sola solicitud que agrega oferta, calcula el precio total entregado (DDP) y ejecuta la compra en un solo sistema. Switzh es la capa transaccional para las compras industriales transfronterizas.",
    pt: "A Switzh substitui o sourcing fragmentado de fornecedores por uma única solicitação que agrega oferta, calcula o preço total entregue (DDP) e executa a compra em um único sistema. A Switzh é a camada transacional para compras industriais transfronteiriças.",
  },

  "hero.bestFor.label": {
    en: "Best for:",
    es: "Ideal para:",
    pt: "Ideal para:",
  },
  "hero.bestFor.1": {
    en: "Urgent replacements",
    es: "Reemplazos urgentes",
    pt: "Substituições urgentes",
  },
  "hero.bestFor.2": {
    en: "Repeat procurement",
    es: "Compras recurrentes",
    pt: "Compras recorrentes",
  },
  "hero.bestFor.3": {
    en: "Hard-to-find parts",
    es: "Piezas difíciles de encontrar",
    pt: "Peças difíceis de encontrar",
  },
  "hero.trustLine": {
    en: "Quotes in <4 hours • Full landed (DDP) pricing upfront • Tracked delivery across LATAM",
    es: "Cotizaciones en <4 horas • Precio total entregado (DDP) por adelantado • Entrega rastreada en toda LATAM",
    pt: "Cotações em <4 horas • Preço total entregue (DDP) adiantado • Entrega rastreada em toda a LATAM",
  },
  "hero.speedLine": {
    en: "Quote turnaround target: under 4 hours",
    es: "Objetivo de respuesta: menos de 4 horas",
    pt: "Meta de resposta: menos de 4 horas",
  },
  "hero.differentiator": {
    en: "Not a catalog. Not a generic agent. One request, sourced across U.S. suppliers — with full landed pricing.",
    es: "No somos un catálogo. No somos un agente genérico. Una solicitud, abastecida entre proveedores de EE. UU. — con precio total entregado.",
    pt: "Não somos um catálogo. Não somos um agente genérico. Uma solicitação, sourced entre fornecedores dos EUA — com preço total entregue.",
  },
  "hero.imageBadge": {
    en: "Quotes in <4 hours",
    es: "Cotizaciones en <4 horas",
    pt: "Cotações em <4 horas",
  },
  "hero.bullet1": {
    en: "Response in minutes — not hours or days",
    es: "Respuesta en minutos — no horas ni días",
    pt: "Resposta em minutos — não horas ou dias",
  },
  "hero.bullet2": {
    en: "Final landed pricing (DDP) upfront",
    es: "Precio total entregado (DDP) por adelantado",
    pt: "Preço total entregue (DDP) antecipado",
  },
  "hero.bullet3": {
    en: "Real availability and lead times",
    es: "Disponibilidad y tiempos de entrega reales",
    pt: "Disponibilidade e prazos de entrega reais",
  },
  "hero.bullet4": {
    en: "Multiple suppliers and equivalent options",
    es: "Múltiples proveedores y opciones equivalentes",
    pt: "Múltiplos fornecedores e opções equivalentes",
  },
  "hero.bullet5": {
    en: "One request replaces 5–10 emails",
    es: "Una solicitud reemplaza 5–10 correos",
    pt: "Uma solicitação substitui 5–10 e-mails",
  },
  "hero.trustMicro": {
    en: "Used by contractors and procurement teams sourcing across LATAM.",
    es: "Usado por contratistas y equipos de compras que abastecen en toda LATAM.",
    pt: "Usado por contratantes e equipes de compras que adquirem em toda a LATAM.",
  },
  "hero.delivery": {
    en: "Typical delivery:",
    es: "Entrega típica:",
    pt: "Entrega típica:",
  },
  "hero.deliveryRange": {
    en: "5–10 business days",
    es: "5–10 días hábiles",
    pt: "5–10 dias úteis",
  },
  "hero.deliverySuffix": {
    en: "",
    es: "",
    pt: "",
  },
  "hero.avgResponse": {
    en: "Avg. response",
    es: "Respuesta prom.",
    pt: "Resposta méd.",
  },
  "hero.avgResponseValue": {
    en: "< 4 hours",
    es: "< 4 horas",
    pt: "< 4 horas",
  },
  "hero.carousel.eyebrow": {
    en: "What we source",
    es: "Lo que abastecemos",
    pt: "O que fornecemos",
  },
  "hero.slide.components.caption": {
    en: "Fast Access Power Components",
    es: "Componentes de Energía de Acceso Rápido",
    pt: "Componentes de Energia de Acesso Rápido",
  },
  "hero.slide.components.alt": {
    en: "Technician installing molded-case circuit breakers (Siemens 3VA, Schneider NSX, Eaton FD, ABB Tmax) inside a low-voltage switchgear cabinet — high-frequency replacement components sourced from U.S. supply for LATAM industrial buyers",
    es: "Técnico instalando interruptores MCCB (Siemens 3VA, Schneider NSX, Eaton FD, ABB Tmax) dentro de un gabinete de switchgear de baja tensión — componentes de reemplazo de alta frecuencia desde EE. UU. para compradores industriales en LATAM",
    pt: "Técnico instalando disjuntores MCCB (Siemens 3VA, Schneider NSX, Eaton FD, ABB Tmax) dentro de um painel de switchgear de baixa tensão — componentes de substituição de alta frequência dos EUA para compradores industriais na LATAM",
  },
  "hero.slide.packages.caption": {
    en: "Pre-Configured Power Packages",
    es: "Paquetes de Energía Pre-Configurados",
    pt: "Pacotes de Energia Pré-Configurados",
  },
  "hero.slide.packages.alt": {
    en: "Bundled circuit breakers, contactors, overload relays and busbars staged on a U.S. distribution warehouse workbench — pre-configured power packages ready for LATAM delivery",
    es: "Interruptores, contactores, relés de sobrecarga y barras agrupados en un almacén de distribución en EE. UU. — paquetes de energía pre-configurados listos para entrega en LATAM",
    pt: "Disjuntores, contatores, relés de sobrecarga e barramentos agrupados em um armazém de distribuição nos EUA — pacotes de energia pré-configurados prontos para entrega na LATAM",
  },
  "hero.slide.systems.caption": {
    en: "Power Distribution Solutions",
    es: "Soluciones de Distribución de Energía",
    pt: "Soluções de Distribuição de Energia",
  },
  "hero.slide.systems.alt": {
    en: "Row of UL 891 low-voltage switchboards and ATS cabinets installed in a clean industrial electrical equipment room — made-to-order U.S.-standard power distribution systems delivered into LATAM",
    es: "Fila de tableros UL 891 de baja tensión y gabinetes ATS instalados en una sala eléctrica industrial — sistemas de distribución de energía estándar EE. UU. hechos por pedido y entregados en LATAM",
    pt: "Fileira de painéis UL 891 de baixa tensão e gabinetes ATS instalados em uma sala elétrica industrial — sistemas de distribuição de energia padrão EUA feitos sob encomenda e entregues na LATAM",
  },

  // ───────── ValueProps ─────────
  "values.h2": {
    en: "One request replaces 5–10 supplier emails",
    es: "Una solicitud reemplaza 5–10 correos a proveedores",
    pt: "Uma solicitação substitui 5–10 e-mails a fornecedores",
  },
  "values.subtitle": {
    en: "Send once — sourced across U.S. suppliers, with full landed pricing, real availability, and equivalent options included.",
    es: "Envía una vez — abastecido entre proveedores de EE. UU., con precio total entregado, disponibilidad real y opciones equivalentes incluidas.",
    pt: "Envie uma vez — sourced entre fornecedores dos EUA, com preço total entregue, disponibilidade real e opções equivalentes incluídas.",
  },
  "values.1.title": {
    en: "Send once — replace 5–10 supplier emails",
    es: "Envía una vez — reemplaza 5–10 correos a proveedores",
    pt: "Envie uma vez — substitua 5–10 e-mails a fornecedores",
  },
  "values.1.text": {
    en: "One structured request, sourced across multiple U.S. suppliers — no chasing, no fragmented quotes.",
    es: "Una solicitud estructurada, abastecida entre múltiples proveedores de EE. UU. — sin perseguir, sin cotizaciones fragmentadas.",
    pt: "Uma solicitação estruturada, sourced entre múltiplos fornecedores dos EUA — sem perseguir, sem cotações fragmentadas.",
  },
  "values.2.title": {
    en: "True landed cost upfront",
    es: "Costo total entregado por adelantado",
    pt: "Custo total entregue antecipado",
  },
  "values.2.text": {
    en: "Product, shipping, duties, and final-mile delivery in one DDP price. No hidden fees or surprises.",
    es: "Producto, envío, aranceles y entrega final en un solo precio DDP. Sin costos ocultos ni sorpresas.",
    pt: "Produto, envio, taxas e entrega final em um único preço DDP. Sem custos ocultos ou surpresas.",
  },
  "values.3.title": {
    en: "Real availability across U.S. suppliers",
    es: "Disponibilidad real entre proveedores de EE. UU.",
    pt: "Disponibilidade real entre fornecedores dos EUA",
  },
  "values.3.text": {
    en: "Lead times pulled from multiple suppliers — not limited to a single distributor or catalog.",
    es: "Tiempos de entrega de múltiples proveedores — no limitados a un solo distribuidor o catálogo.",
    pt: "Prazos de entrega de múltiplos fornecedores — não limitados a um único distribuidor ou catálogo.",
  },
  "values.4.title": {
    en: "Validated specs and equivalent options",
    es: "Especificaciones validadas y opciones equivalentes",
    pt: "Especificações validadas e opções equivalentes",
  },
  "values.4.text": {
    en: "Compatible replacements suggested when applicable — predictable delivery timelines either way.",
    es: "Reemplazos compatibles sugeridos cuando aplica — plazos de entrega predecibles en cualquier caso.",
    pt: "Substituições compatíveis sugeridas quando aplicável — prazos de entrega previsíveis em qualquer caso.",
  },

  // ───────── TrustStrip ─────────
  "trust.h2": {
    en: "Trusted by electrical contractors, maintenance teams, and procurement teams across LATAM",
    es: "Confiado por contratistas eléctricos, equipos de mantenimiento y equipos de compras en toda LATAM",
    pt: "Confiado por contratantes elétricos, equipes de manutenção e equipes de compras em toda a LATAM",
  },
  "trust.1": {
    en: "Quote turnaround target: under 4 hours",
    es: "Objetivo de respuesta: menos de 4 horas",
    pt: "Meta de resposta: menos de 4 horas",
  },
  "trust.2": {
    en: "Full landed (DDP) cost before you commit",
    es: "Costo total entregado (DDP) antes de comprometerte",
    pt: "Custo total entregue (DDP) antes de se comprometer",
  },
  "trust.3": {
    en: "Tracked execution from request to site delivery",
    es: "Ejecución rastreada desde la solicitud hasta la entrega",
    pt: "Execução rastreada do pedido à entrega no local",
  },
  "trust.4": {
    en: "Substitute recommendations when parts are unavailable",
    es: "Recomendaciones de sustitutos cuando la pieza no está disponible",
    pt: "Recomendações de substitutos quando a peça não está disponível",
  },
  "trust.footer": {
    en: "Focused on switchgear, breakers, contactors, and protection devices — sourced from verified U.S. distributors and delivered DDP across LATAM.",
    es: "Enfocados en switchgear, interruptores, contactores y dispositivos de protección — suministrados por distribuidores verificados de EE. UU. y entregados DDP en toda LATAM.",
    pt: "Focados em switchgear, disjuntores, contatores e dispositivos de proteção — fornecidos por distribuidores verificados dos EUA e entregues DDP em toda a LATAM.",
  },

  // ───────── Suppliers ─────────
  "suppliers.eyebrow": {
    en: "Brands we source",
    es: "Marcas que suministramos",
    pt: "Marcas que fornecemos",
  },
  "suppliers.h2": {
    en: "U.S. electrical manufacturers — ABB, Schneider, Eaton & more",
    es: "Fabricantes eléctricos de EE. UU. — ABB, Schneider, Eaton y más",
    pt: "Fabricantes elétricos dos EUA — ABB, Schneider, Eaton e mais",
  },
  "suppliers.subtitle": {
    en: "We source components across a network of leading U.S. manufacturers and authorized distributors to secure availability and reduce lead times across LATAM.",
    es: "Suministramos componentes a través de una red de fabricantes líderes y distribuidores autorizados de EE. UU. para asegurar disponibilidad y reducir plazos en toda LATAM.",
    pt: "Fornecemos componentes através de uma rede de fabricantes líderes e distribuidores autorizados nos EUA para garantir disponibilidade e reduzir prazos em toda a LATAM.",
  },
  "suppliers.note": {
    en: "Supplier selection is based on availability, specifications, and delivery timeline across our extended U.S. network.",
    es: "La selección de proveedor se basa en disponibilidad, especificaciones y plazo de entrega dentro de nuestra red ampliada en EE. UU.",
    pt: "A seleção do fornecedor é baseada em disponibilidade, especificações e prazo de entrega dentro da nossa rede estendida nos EUA.",
  },

  // ───────── Categories ─────────
  "cat.eyebrow": {
    en: "Fast Access Power Components",
    es: "Componentes de Energía de Acceso Rápido",
    pt: "Componentes de Energia de Acesso Rápido",
  },
  "cat.h2": {
    en: "U.S. switchgear components, delivered to LATAM",
    es: "Componentes de switchgear de EE. UU., entregados en LATAM",
    pt: "Componentes de switchgear dos EUA, entregues na LATAM",
  },
  "cat.subtitle": {
    en: "Top 50 SKUs from Siemens, Schneider Electric, Eaton, and ABB — quoted in <4 hours with full landed pricing (DDP).",
    es: "Top 50 SKUs de Siemens, Schneider Electric, Eaton y ABB — cotizados en <4 horas con precio total entregado (DDP).",
    pt: "Top 50 SKUs de Siemens, Schneider Electric, Eaton e ABB — cotados em <4 horas com preço total entregue (DDP).",
  },
  "cat.1.title": {
    en: "Circuit Breakers",
    es: "Interruptores",
    pt: "Disjuntores",
  },
  "cat.1.text": {
    en: "Siemens 3VA, Schneider NSX, Eaton FD, ABB Tmax — 100A–630A, sourced for urgent replacement.",
    es: "Siemens 3VA, Schneider NSX, Eaton FD, ABB Tmax — 100A–630A, abastecidos para reemplazo urgente.",
    pt: "Siemens 3VA, Schneider NSX, Eaton FD, ABB Tmax — 100A–630A, fornecidos para substituição urgente.",
  },
  "cat.2.title": {
    en: "Contactors + Relays",
    es: "Contactores + Relés",
    pt: "Contatores + Relés",
  },
  "cat.2.text": {
    en: "Schneider TeSys (LC1D), Siemens 3RT, ABB AF, Eaton XTCE — control system replacement.",
    es: "Schneider TeSys (LC1D), Siemens 3RT, ABB AF, Eaton XTCE — reemplazo en sistemas de control.",
    pt: "Schneider TeSys (LC1D), Siemens 3RT, ABB AF, Eaton XTCE — substituição em sistemas de controle.",
  },
  "cat.3.title": {
    en: "Disconnect Switches",
    es: "Interruptores de Desconexión",
    pt: "Chaves Seccionadoras",
  },
  "cat.3.text": {
    en: "Siemens HNF, Schneider HU, Eaton DH — safety isolation and regulatory replacement.",
    es: "Siemens HNF, Schneider HU, Eaton DH — aislamiento de seguridad y reemplazo regulatorio.",
    pt: "Siemens HNF, Schneider HU, Eaton DH — isolamento de segurança e substituição regulatória.",
  },
  "cat.4.title": {
    en: "Protection Devices",
    es: "Dispositivos de Protección",
    pt: "Dispositivos de Proteção",
  },
  "cat.4.text": {
    en: "Schneider LRD, Siemens 3RU, ABB TA overload relays + A9L / 5SD surge protection (SPD).",
    es: "Relés de sobrecarga Schneider LRD, Siemens 3RU, ABB TA + protección contra sobretensiones A9L / 5SD (SPD).",
    pt: "Relés de sobrecarga Schneider LRD, Siemens 3RU, ABB TA + proteção contra surtos A9L / 5SD (SPD).",
  },
  "cat.5.title": {
    en: "ATS Components",
    es: "Componentes de ATS",
    pt: "Componentes de ATS",
  },
  "cat.5.text": {
    en: "Transfer-switch parts and backup-power replacements sourced from U.S. supply.",
    es: "Repuestos para interruptores de transferencia y respaldo de energía desde EE. UU.",
    pt: "Peças para chaves de transferência e backup de energia a partir do fornecimento dos EUA.",
  },
  "cat.6.title": {
    en: "Accessories (Busbars + Connectors)",
    es: "Accesorios (Barras + Conectores)",
    pt: "Acessórios (Barramentos + Conectores)",
  },
  "cat.6.text": {
    en: "Busbar kits, connectors, and supporting hardware bundled with primary orders.",
    es: "Kits de barras, conectores y herrajes de soporte agrupados con pedidos principales.",
    pt: "Kits de barramento, conectores e ferragens agrupados com pedidos principais.",
  },
  "cat.bottom.prefix": {
    en: "Most requests are for",
    es: "La mayoría de solicitudes son para",
    pt: "A maioria dos pedidos é para",
  },
  "cat.bottom.highlight": {
    en: "urgent, repeat, and hard-to-source",
    es: "urgentes, recurrentes y difíciles de conseguir",
    pt: "urgentes, recorrentes e difíceis de encontrar",
  },
  "cat.bottom.suffix": { en: "components.", es: "componentes.", pt: "componentes." },
  "cat.related.prefix": {
    en: "Popular requests:",
    es: "Solicitudes populares:",
    pt: "Solicitações populares:",
  },

  // ───────── Comparison ─────────
  "cmp.eyebrow": {
    en: "What makes us different",
    es: "Qué nos hace diferentes",
    pt: "O que nos diferencia",
  },
  "cmp.h2": {
    en: "Not a distributor — a faster access layer",
    es: "No somos un distribuidor — somos una capa de acceso más rápida",
    pt: "Não somos um distribuidor — somos uma camada de acesso mais rápida",
  },
  "cmp.lead1": {
    en: "Traditional sourcing relies on a single distributor. If they're out of stock or slow to respond, your project waits.",
    es: "El abastecimiento tradicional depende de un solo distribuidor. Si está agotado o tarda en responder, tu proyecto espera.",
    pt: "O sourcing tradicional depende de um único distribuidor. Se ele estiver sem estoque ou demorar a responder, seu projeto espera.",
  },
  "cmp.lead2.prefix": {
    en: "We check",
    es: "Consultamos",
    pt: "Consultamos",
  },
  "cmp.lead2.bold": {
    en: "multiple verified U.S. suppliers at once",
    es: "múltiples proveedores verificados de EE. UU. a la vez",
    pt: "múltiplos fornecedores verificados dos EUA ao mesmo tempo",
  },
  "cmp.lead2.suffix": {
    en: "to find the fastest available option.",
    es: "para encontrar la opción más rápida disponible.",
    pt: "para encontrar a opção mais rápida disponível.",
  },
  "cmp.trad.label": {
    en: "Traditional distributor",
    es: "Distribuidor tradicional",
    pt: "Distribuidor tradicional",
  },
  "cmp.trad.title": {
    en: "One supplier, one outcome",
    es: "Un proveedor, un resultado",
    pt: "Um fornecedor, um resultado",
  },
  "cmp.trad.1": {
    en: "Single supplier — single point of failure",
    es: "Un solo proveedor — un único punto de falla",
    pt: "Um único fornecedor — um único ponto de falha",
  },
  "cmp.trad.2": {
    en: "Slow response (often days)",
    es: "Respuesta lenta (a menudo días)",
    pt: "Resposta lenta (frequentemente dias)",
  },
  "cmp.trad.3": {
    en: "Limited inventory for hard-to-find parts",
    es: "Inventario limitado para piezas difíciles",
    pt: "Estoque limitado para peças difíceis",
  },
  "cmp.trad.4": {
    en: "Hidden costs at customs and freight",
    es: "Costos ocultos en aduanas y flete",
    pt: "Custos ocultos na alfândega e no frete",
  },
  "cmp.pps.title": {
    en: "Multi-supplier sourcing, faster outcomes",
    es: "Abastecimiento multi-proveedor, resultados más rápidos",
    pt: "Sourcing multi-fornecedor, resultados mais rápidos",
  },
  "cmp.pps.1": {
    en: "Multiple verified U.S. suppliers — not just one",
    es: "Múltiples proveedores verificados de EE. UU. — no solo uno",
    pt: "Múltiplos fornecedores verificados dos EUA — não apenas um",
  },
  "cmp.pps.2": {
    en: "Response in hours, not days",
    es: "Respuesta en horas, no días",
    pt: "Resposta em horas, não dias",
  },
  "cmp.pps.3": {
    en: "Focused on hard-to-find and urgent components",
    es: "Enfocados en componentes difíciles y urgentes",
    pt: "Foco em componentes difíceis e urgentes",
  },
  "cmp.pps.4": {
    en: "Full landed (DDP) pricing upfront — no surprises",
    es: "Precio total puesto (DDP) por adelantado — sin sorpresas",
    pt: "Preço total entregue (DDP) adiantado — sem surpresas",
  },
  "cmp.quote": {
    en: "“If one supplier can't deliver, we find one that can.”",
    es: "“Si un proveedor no puede entregar, encontramos uno que sí.”",
    pt: "“Se um fornecedor não puder entregar, encontramos um que pode.”",
  },

  // ───────── WhenToUse ─────────
  "use.eyebrow": { en: "Use cases", es: "Casos de uso", pt: "Casos de uso" },
  "use.h2": {
    en: "When to use Switzh",
    es: "Cuándo usar Switzh",
    pt: "Quando usar a Switzh",
  },
  "use.subtitle": {
    en: "Reach out when local supply runs out, lead times stretch, or a project deadline is at risk.",
    es: "Contáctanos cuando se agote el stock local, los plazos se alarguen o un proyecto esté en riesgo.",
    pt: "Fale conosco quando o estoque local acabar, os prazos esticarem ou um projeto estiver em risco.",
  },
  "use.1": {
    en: "Your supplier is out of stock",
    es: "Tu proveedor no tiene stock",
    pt: "Seu fornecedor está sem estoque",
  },
  "use.2": {
    en: "Lead times are too long (6–16+ weeks)",
    es: "Plazos demasiado largos (6–16+ semanas)",
    pt: "Prazos muito longos (6–16+ semanas)",
  },
  "use.3": {
    en: "You need a replacement part urgently",
    es: "Necesitas una pieza de repuesto con urgencia",
    pt: "Você precisa de uma peça de reposição com urgência",
  },
  "use.4": {
    en: "You can't find a specific SKU locally",
    es: "No encuentras un SKU específico localmente",
    pt: "Não encontra um SKU específico localmente",
  },
  "use.5": {
    en: "Need full landed pricing (DDP) upfront",
    es: "Necesitas precio total final (DDP) por adelantado",
    pt: "Precisa do preço total final (DDP) adiantado",
  },
  "use.6": {
    en: "Need a <4-hour quote with DDP delivery to LATAM",
    es: "Necesitas cotización en <4 horas con entrega DDP a LATAM",
    pt: "Precisa de cotação em <4 horas com entrega DDP à LATAM",
  },

  // ───────── HowItWorks ─────────
  "how.eyebrow": { en: "How it works", es: "Cómo funciona", pt: "Como funciona" },
  "how.h2": {
    en: "From request to delivery in 5 steps",
    es: "De la solicitud a la entrega en 5 pasos",
    pt: "Do pedido à entrega em 5 passos",
  },
  "how.step": { en: "Step", es: "Paso", pt: "Passo" },
  "how.1.title": {
    en: "Submit request",
    es: "Envía solicitud",
    pt: "Envie a solicitação",
  },
  "how.1.text": {
    en: "Buyer submits RFQ — SKU, link, or short description.",
    es: "El comprador envía la RFQ — SKU, enlace o descripción breve.",
    pt: "O comprador envia a RFQ — SKU, link ou descrição breve.",
  },
  "how.2.title": {
    en: "We aggregate supplier options",
    es: "Agregamos opciones de proveedores",
    pt: "Agregamos opções de fornecedores",
  },
  "how.2.text": {
    en: "Switzh aggregates supplier options and confirms availability across our network (Sonepar and others).",
    es: "Switzh agrega opciones de proveedores y confirma disponibilidad en nuestra red (Sonepar y otros).",
    pt: "A Switzh agrega opções de fornecedores e confirma disponibilidade na nossa rede (Sonepar e outros).",
  },
  "how.3.title": {
    en: "Calculate DDP landed cost",
    es: "Calculamos costo DDP final",
    pt: "Calculamos custo DDP final",
  },
  "how.3.text": {
    en: "System calculates full landed (DDP) pricing — shipping, duties, and taxes included.",
    es: "El sistema calcula el precio total final (DDP) — envío, aranceles e impuestos incluidos.",
    pt: "O sistema calcula o preço total final (DDP) — frete, taxas e impostos incluídos.",
  },
  "how.4.title": {
    en: "Structured quote in <4 hours",
    es: "Cotización estructurada en <4 horas",
    pt: "Cotação estruturada em <4 horas",
  },
  "how.4.text": {
    en: "Buyer receives a structured quote with confirmed pricing and delivery — typically under 4 hours.",
    es: "El comprador recibe una cotización estructurada con precio y entrega confirmados — normalmente en menos de 4 horas.",
    pt: "O comprador recebe uma cotação estruturada com preço e entrega confirmados — normalmente em menos de 4 horas.",
  },
  "how.5.title": {
    en: "Order → procurement → delivery",
    es: "Pedido → compra → entrega",
    pt: "Pedido → compra → entrega",
  },
  "how.5.text": {
    en: "Order confirmed — Switzh executes procurement and cross-border delivery via integrated logistics (SkyPostal).",
    es: "Pedido confirmado — Switzh ejecuta la compra y la entrega transfronteriza con logística integrada (SkyPostal).",
    pt: "Pedido confirmado — a Switzh executa a compra e a entrega transfronteiriça com logística integrada (SkyPostal).",
  },
  "how.note.prefix": { en: "Quote turnaround target:", es: "Objetivo de respuesta:", pt: "Meta de resposta:" },
  "how.note.bold": {
    en: "under 4 hours",
    es: "menos de 4 horas",
    pt: "menos de 4 horas",
  },

  // ───────── Timelines ─────────
  "tl.eyebrow": { en: "Delivery", es: "Entrega", pt: "Entrega" },
  "tl.h2": {
    en: "Delivery timelines",
    es: "Plazos de entrega",
    pt: "Prazos de entrega",
  },
  "tl.express.label": { en: "Express", es: "Express", pt: "Express" },
  "tl.express.desc": {
    en: "For urgent and time-critical sourcing.",
    es: "Para abastecimiento urgente y crítico en tiempo.",
    pt: "Para sourcing urgente e crítico em tempo.",
  },
  "tl.standard.label": { en: "Standard", es: "Estándar", pt: "Padrão" },
  "tl.standard.desc": {
    en: "Optimized cost with 5–10 day DDP delivery.",
    es: "Costo optimizado con entrega DDP de 5–10 días.",
    pt: "Custo otimizado com entrega DDP de 5–10 dias.",
  },
  "tl.unit": { en: "days", es: "días", pt: "dias" },
  "tl.note": {
    en: "Based on U.S. inventory availability. Final timeline confirmed per quote, from order confirmation.",
    es: "Sujeto a disponibilidad de inventario en EE. UU. Plazo final confirmado por cotización, desde la confirmación del pedido.",
    pt: "Sujeito à disponibilidade de estoque nos EUA. Prazo final confirmado por cotação, a partir da confirmação do pedido.",
  },
  "tl.proof": {
    en: "Sourced from verified U.S. distributors · DDP delivery across LATAM",
    es: "Abastecido desde distribuidores verificados en EE. UU. · Entrega DDP en toda LATAM",
    pt: "Fornecido por distribuidores verificados nos EUA · Entrega DDP em toda a LATAM",
  },

  // ───────── CountryCoverage ─────────
  "geo.eyebrow": {
    en: "LATAM coverage",
    es: "Cobertura en LATAM",
    pt: "Cobertura na LATAM",
  },
  "geo.h2": {
    en: "5–10 day DDP delivery across LATAM, fully managed",
    es: "Entrega DDP de 5–10 días en toda LATAM, totalmente gestionada",
    pt: "Entrega DDP de 5–10 dias em toda a LATAM, totalmente gerenciada",
  },
  "geo.subtitle": {
    en: "DDP delivery with import clearance, duties, and local logistics fully managed — from U.S. sourcing to final delivery.",
    es: "Entrega DDP con despacho de importación, aranceles y logística local totalmente gestionados — desde el abastecimiento en EE. UU. hasta la entrega final.",
    pt: "Entrega DDP com desembaraço de importação, impostos e logística local totalmente gerenciados — do sourcing nos EUA à entrega final.",
  },
  "geo.ops": {
    en: "We handle international shipping, customs clearance, and last-mile delivery.",
    es: "Gestionamos el envío internacional, el despacho aduanero y la entrega de última milla.",
    pt: "Cuidamos do envio internacional, do desembaraço aduaneiro e da entrega de última milha.",
  },
  "geo.routesLabel": {
    en: "Active delivery routes include:",
    es: "Rutas activas de entrega incluyen:",
    pt: "Rotas ativas de entrega incluem:",
  },
  "geo.trustMicro": {
    en: "Consistent delivery across contractors, EPCs, and industrial buyers",
    es: "Entrega consistente para contratistas, EPCs y compradores industriales",
    pt: "Entrega consistente para empreiteiras, EPCs e compradores industriais",
  },

  // ───────── CaseStudy ─────────
  "case.eyebrow": {
    en: "Real use cases",
    es: "Casos reales",
    pt: "Casos reais",
  },
  "case.prev": { en: "Previous case", es: "Caso anterior", pt: "Caso anterior" },
  "case.next": { en: "Next case", es: "Caso siguiente", pt: "Próximo caso" },
  "case.proof.contractor": {
    en: "Real case — contractor",
    es: "Caso real — contratista",
    pt: "Caso real — empreiteira",
  },
  "case.proof.epc": {
    en: "Real case — EPC",
    es: "Caso real — EPC",
    pt: "Caso real — EPC",
  },
  "case.proof.industrial": {
    en: "Real case — industrial operator",
    es: "Caso real — operador industrial",
    pt: "Caso real — operador industrial",
  },
  "case.proof.utility": {
    en: "Real case — utility project",
    es: "Caso real — proyecto de utility",
    pt: "Caso real — projeto de utility",
  },
  "case.fromOrder": {
    en: "from order confirmation",
    es: "desde la confirmación del pedido",
    pt: "a partir da confirmação do pedido",
  },

  // Case 1 — Colombia / MCCB
  "case1.headline": {
    en: "A contractor in Colombia needed a Schneider Electric MCCB unavailable locally.",
    es: "Un contratista en Colombia necesitaba un MCCB Schneider Electric no disponible localmente.",
    pt: "Uma empreiteira na Colômbia precisava de um MCCB Schneider Electric indisponível localmente.",
  },
  "case1.sub": {
    en: "Switzh sourced the exact unit from U.S. inventory and delivered in 5 days — avoiding a 4–6 week delay.",
    es: "Switzh suministró la unidad exacta desde inventario en EE. UU. y la entregó en 5 días — evitando un retraso de 4 a 6 semanas.",
    pt: "Switzh forneceu a unidade exata do estoque dos EUA e entregou em 5 dias — evitando um atraso de 4 a 6 semanas.",
  },
  "case1.m1": { en: "5 Days", es: "5 Días", pt: "5 Dias" },
  "case1.m1.sub": { en: "Instead of 4–6 weeks", es: "En lugar de 4 a 6 semanas", pt: "Em vez de 4 a 6 semanas" },
  "case1.m2": { en: "1 Critical SKU", es: "1 SKU crítico", pt: "1 SKU crítico" },
  "case1.m2.sub": { en: "Schneider Electric MCCB", es: "MCCB Schneider Electric", pt: "MCCB Schneider Electric" },
  "case1.m3": { en: "On Time", es: "A tiempo", pt: "No prazo" },
  "case1.m3.sub": { en: "Project schedule maintained", es: "Cronograma del proyecto mantenido", pt: "Cronograma do projeto mantido" },

  // Case 2 — Peru / Siemens relay
  "case2.headline": {
    en: "A mining operator needed an obsolete Siemens relay no longer available locally.",
    es: "Un operador minero necesitaba un relé Siemens obsoleto que ya no estaba disponible localmente.",
    pt: "Uma operadora de mineração precisava de um relé Siemens obsoleto sem disponibilidade local.",
  },
  "case2.sub": {
    en: "Switzh located remaining U.S. stock and delivered in 6 days — preventing operational downtime.",
    es: "Switzh localizó el stock restante en EE. UU. y entregó en 6 días — evitando paradas operativas.",
    pt: "Switzh localizou o estoque remanescente nos EUA e entregou em 6 dias — evitando paradas operacionais.",
  },
  "case2.m1": { en: "6 Days", es: "6 Días", pt: "6 Dias" },
  "case2.m1.sub": { en: "Instead of indefinite delay", es: "En lugar de retraso indefinido", pt: "Em vez de atraso indefinido" },
  "case2.m2": { en: "1 Obsolete Part", es: "1 Pieza obsoleta", pt: "1 Peça obsoleta" },
  "case2.m2.sub": { en: "Siemens relay", es: "Relé Siemens", pt: "Relé Siemens" },
  "case2.m3": { en: "Zero Downtime", es: "Cero paradas", pt: "Zero paradas" },
  "case2.m3.sub": { en: "Operations continued", es: "Operaciones continuaron", pt: "Operações continuaram" },

  // Case 3 — Mexico / Panelboard
  "case3.headline": {
    en: "An EPC contractor required a panelboard with urgent delivery to meet installation deadlines.",
    es: "Un contratista EPC requería un tablero de distribución con entrega urgente para cumplir los plazos de instalación.",
    pt: "Uma empreiteira EPC precisava de um quadro de distribuição com entrega urgente para cumprir os prazos de instalação.",
  },
  "case3.sub": {
    en: "Switzh sourced from U.S. distributor inventory and delivered in 7 days.",
    es: "Switzh suministró desde inventario de distribuidor en EE. UU. y entregó en 7 días.",
    pt: "Switzh forneceu do estoque de distribuidor dos EUA e entregou em 7 dias.",
  },
  "case3.m1": { en: "7 Days", es: "7 Días", pt: "7 Dias" },
  "case3.m1.sub": { en: "Instead of 3–5 weeks", es: "En lugar de 3 a 5 semanas", pt: "Em vez de 3 a 5 semanas" },
  "case3.m2": { en: "1 Panelboard", es: "1 Tablero", pt: "1 Quadro" },
  "case3.m2.sub": { en: "Industrial distribution", es: "Distribución industrial", pt: "Distribuição industrial" },
  "case3.m3": { en: "On Schedule", es: "En cronograma", pt: "No cronograma" },
  "case3.m3.sub": { en: "Installation completed", es: "Instalación completada", pt: "Instalação concluída" },

  // Case 4 — Chile / RMU
  "case4.headline": {
    en: "A utility project required an RMU with limited regional availability.",
    es: "Un proyecto de utility requería un RMU con disponibilidad regional limitada.",
    pt: "Um projeto de utility precisava de um RMU com disponibilidade regional limitada.",
  },
  "case4.sub": {
    en: "Switzh secured available U.S. stock and delivered in under 10 days.",
    es: "Switzh aseguró el stock disponible en EE. UU. y entregó en menos de 10 días.",
    pt: "Switzh garantiu o estoque disponível nos EUA e entregou em menos de 10 dias.",
  },
  "case4.m1": { en: "10 Days", es: "10 Días", pt: "10 Dias" },
  "case4.m1.sub": { en: "Instead of 6–8 weeks", es: "En lugar de 6 a 8 semanas", pt: "Em vez de 6 a 8 semanas" },
  "case4.m2": { en: "1 RMU", es: "1 RMU", pt: "1 RMU" },
  "case4.m2.sub": { en: "Medium voltage distribution", es: "Distribución media tensión", pt: "Distribuição média tensão" },
  "case4.m3": { en: "Project Secured", es: "Proyecto asegurado", pt: "Projeto garantido" },
  "case4.m3.sub": { en: "Timeline maintained", es: "Cronograma mantenido", pt: "Cronograma mantido" },

  // ───────── QuoteForm ─────────
  "qf.eyebrow": { en: "Get pricing", es: "Obtenga su precio", pt: "Obtenha seu preço" },
  "qf.h2": {
    en: "Request a Quote",
    es: "Solicitar Cotización",
    pt: "Solicitar Cotação",
  },
  "qf.subtitle": {
    en: "Get pricing, availability, and lead times for your request.",
    es: "Recibe precios, disponibilidad y tiempos de entrega para tu solicitud.",
    pt: "Receba preços, disponibilidade e prazos de entrega para sua solicitação.",
  },
  "qf.urgencyLine": {
    en: "Send once — sourced across U.S. suppliers.",
    es: "Envía una vez — abastecido en proveedores de EE. UU.",
    pt: "Envie uma vez — sourcing em fornecedores dos EUA.",
  },
  "qf.capabilityLine": {
    en: "We identify matching products, equivalents, and delivery options — automatically.",
    es: "Identificamos productos equivalentes, alternativas y opciones de entrega — automáticamente.",
    pt: "Identificamos produtos equivalentes, alternativas e opções de entrega — automaticamente.",
  },
  // Response time block (above form)
  "qf.responseBlock.title": {
    en: "Expected response time",
    es: "Tiempo de respuesta esperado",
    pt: "Tempo de resposta esperado",
  },
  "qf.responseBlock.high": {
    en: "High Priority (urgent): response within 4 hours",
    es: "Alta Prioridad (urgente): respuesta en menos de 4 horas",
    pt: "Alta Prioridade (urgente): resposta em até 4 horas",
  },
  "qf.responseBlock.standard": {
    en: "Standard: response within 24 hours",
    es: "Estándar: respuesta en menos de 24 horas",
    pt: "Padrão: resposta em até 24 horas",
  },
  // Request type field
  "qf.requestType": {
    en: "What are you requesting?",
    es: "¿Qué estás solicitando?",
    pt: "O que você está solicitando?",
  },
  "qf.requestType.ph": {
    en: "Select request type",
    es: "Seleccione tipo de solicitud",
    pt: "Selecione o tipo de solicitação",
  },
  "qf.requestType.kit": {
    en: "Pre-Configured Power Packages",
    es: "Paquetes de potencia preconfigurados",
    pt: "Pacotes de energia pré-configurados",
  },
  "qf.requestType.components": {
    en: "Fast Access Power Components",
    es: "Componentes eléctricos de acceso rápido",
    pt: "Componentes elétricos de acesso rápido",
  },
  "qf.requestType.system": {
    en: "Power Distribution Solutions",
    es: "Soluciones de distribución eléctrica",
    pt: "Soluções de distribuição elétrica",
  },
  "qf.requestType.other": {
    en: "Not sure / Other",
    es: "No estoy seguro / Otro",
    pt: "Não tenho certeza / Outro",
  },
  // Updated main input
  "qf.product.main": {
    en: "What do you need sourced?",
    es: "¿Qué necesita abastecer?",
    pt: "O que você precisa sourcear?",
  },
  "qf.product.main.helper": {
    en: "Paste SKU, link, or description — we analyze specs, compatibility, and equivalents automatically.",
    es: "Pegue SKU, enlace o descripción — analizamos especificaciones, compatibilidad y equivalentes automáticamente.",
    pt: "Cole SKU, link ou descrição — analisamos especificações, compatibilidade e equivalentes automaticamente.",
  },
  // Updated CTA
  "qf.submit.pricingAvailability": {
    en: "Get Pricing & Availability",
    es: "Obtener Precio y Disponibilidad",
    pt: "Obter Preço e Disponibilidade",
  },
  // Trust line
  "qf.trustNetwork": {
    en: "Sourced from verified U.S. distributors. Used by contractors and procurement teams across LATAM.",
    es: "Abastecido desde distribuidores verificados de EE. UU. Usado por contratistas y equipos de compras en toda LATAM.",
    pt: "Sourcing de distribuidores verificados dos EUA. Usado por empreiteiros e equipes de compras em toda a LATAM.",
  },
  // WhatsApp option line
  "qf.waPreferFaster": {
    en: "Prefer faster response? Send via WhatsApp",
    es: "¿Prefiere respuesta más rápida? Envíe por WhatsApp",
    pt: "Prefere resposta mais rápida? Envie pelo WhatsApp",
  },
  "qf.contactHint": {
    en: "Email or WhatsApp — provide at least one.",
    es: "Correo o WhatsApp — indique al menos uno.",
    pt: "E-mail ou WhatsApp — informe ao menos um.",
  },
  "qf.waCta": {
    en: "Or send it on WhatsApp",
    es: "O envíelo por WhatsApp",
    pt: "Ou envie pelo WhatsApp",
  },
  "qf.draft.restored": {
    en: "Draft restored",
    es: "Borrador restaurado",
    pt: "Rascunho restaurado",
  },
  "qf.draft.clear": {
    en: "Clear",
    es: "Borrar",
    pt: "Limpar",
  },
  "qf.draft.dismiss": {
    en: "Dismiss",
    es: "Cerrar",
    pt: "Fechar",
  },
  "qf.trust1": {
    en: "Verified U.S. suppliers",
    es: "Proveedores verificados de EE. UU.",
    pt: "Fornecedores verificados dos EUA",
  },
  "qf.noAccountRequired": {
    en: "No account required. Used by contractors and procurement teams for fast quoting.",
    es: "No requiere cuenta. Utilizado por contratistas y equipos de compras para cotizar rápido.",
    pt: "Não requer conta. Usado por empreiteiros e equipes de compras para cotação rápida.",
  },
  "qf.trust2": {
    en: "Delivered across LATAM",
    es: "Entregado en toda LATAM",
    pt: "Entregue em toda a LATAM",
  },
  "qf.trust3": {
    en: "Full landed cost (DDP) — no hidden fees",
    es: "Costo total final (DDP) — sin cargos ocultos",
    pt: "Custo total final (DDP) — sem taxas ocultas",
  },
  "qf.required": { en: "required", es: "requerido", pt: "obrigatório" },
  "qf.fullName": { en: "Full Name", es: "Nombre Completo", pt: "Nome Completo" },
  "qf.companyName": { en: "Company Name", es: "Nombre de la Empresa", pt: "Nome da Empresa" },
  "qf.email": { en: "Email", es: "Correo Electrónico", pt: "E-mail" },
  "qf.phone": { en: "WhatsApp / Phone", es: "WhatsApp / Teléfono", pt: "WhatsApp / Telefone" },
  "qf.country": { en: "Country", es: "País", pt: "País" },
  "qf.city": { en: "City", es: "Ciudad", pt: "Cidade" },
  "qf.postal": { en: "Postal / Zip Code", es: "Código Postal", pt: "CEP / Código Postal" },
  "qf.quantity": { en: "Quantity", es: "Cantidad", pt: "Quantidade" },
  "qf.product": {
    en: "Part number, link, or description",
    es: "Número de parte, enlace o descripción",
    pt: "Número da peça, link ou descrição",
  },
  "qf.product.ph": {
    en: "e.g. ABB MCCB XT4N, product URL, or description",
    es: "ej. ABB MCCB XT4N, URL del producto o descripción",
    pt: "ex. ABB MCCB XT4N, URL do produto ou descrição",
  },
  "qf.urgency": {
    en: "Delivery Urgency / Required Date",
    es: "Urgencia / Fecha Requerida",
    pt: "Urgência / Data Necessária",
  },
  "qf.urgency.ph": { en: "Select urgency", es: "Seleccione urgencia", pt: "Selecione urgência" },
  "qf.urgency.critical": {
    en: "Critical — needed ASAP",
    es: "Crítica — necesaria ASAP",
    pt: "Crítica — necessária ASAP",
  },
  "qf.urgency.express": {
    en: "Express — within 1 week",
    es: "Express — dentro de 1 semana",
    pt: "Express — dentro de 1 semana",
  },
  "qf.urgency.standard": {
    en: "Standard — 2–3 weeks",
    es: "Estándar — 2–3 semanas",
    pt: "Padrão — 2–3 semanas",
  },
  "qf.urgency.planning": {
    en: "Planning — flexible",
    es: "Planificación — flexible",
    pt: "Planejamento — flexível",
  },
  // Slim form — new urgency labels (3 options spec)
  "qf.urgency.urgent": {
    en: "Urgent — within days",
    es: "Urgente — en días",
    pt: "Urgente — em dias",
  },
  "qf.urgency.soon": {
    en: "Soon — 1–2 weeks",
    es: "Pronto — 1–2 semanas",
    pt: "Em breve — 1–2 semanas",
  },
  "qf.urgency.flexible": {
    en: "Standard — flexible timing",
    es: "Estándar — tiempos flexibles",
    pt: "Padrão — prazo flexível",
  },
  // Slim form — alternative acceptable toggle
  "qf.alternative": {
    en: "Alternative acceptable?",
    es: "¿Acepta equivalente?",
    pt: "Aceita equivalente?",
  },
  "qf.alternative.yes": {
    en: "Yes — open to alternatives",
    es: "Sí — abierto a equivalentes",
    pt: "Sim — aberto a equivalentes",
  },
  "qf.alternative.no": {
    en: "No — exact part required",
    es: "No — requiero la parte exacta",
    pt: "Não — exijo a peça exata",
  },
  // Slim form — country dropdown
  "qf.country.ph": {
    en: "Select country",
    es: "Seleccione país",
    pt: "Selecione país",
  },
  "qf.country.colombia": { en: "Colombia", es: "Colombia", pt: "Colômbia" },
  "qf.country.peru": { en: "Peru", es: "Perú", pt: "Peru" },
  "qf.country.mexico": { en: "Mexico", es: "México", pt: "México" },
  "qf.country.brazil": { en: "Brazil", es: "Brasil", pt: "Brasil" },
  "qf.country.chile": { en: "Chile", es: "Chile", pt: "Chile" },
  "qf.country.ecuador": { en: "Ecuador", es: "Ecuador", pt: "Equador" },
  "qf.country.panama": { en: "Panama", es: "Panamá", pt: "Panamá" },
  "qf.country.costarica": { en: "Costa Rica", es: "Costa Rica", pt: "Costa Rica" },
  "qf.country.dominicanrepublic": { en: "Dominican Republic", es: "República Dominicana", pt: "República Dominicana" },
  "qf.country.other": { en: "Other", es: "Otro", pt: "Outro" },
  // Slim form — section labels
  "qf.section.primary": { en: "What you need", es: "Qué necesitas", pt: "O que você precisa" },
  "qf.section.context": { en: "Context (optional)", es: "Contexto (opcional)", pt: "Contexto (opcional)" },
  "qf.section.delivery": { en: "Delivery", es: "Entrega", pt: "Entrega" },
  "qf.section.contact": { en: "Contact", es: "Contacto", pt: "Contato" },
  "qf.section.productDetails": {
    en: "Product Details",
    es: "Detalles del producto",
    pt: "Detalhes do produto",
  },
  "qf.section.primary.micro": {
    en: "Takes less than 2 minutes to submit",
    es: "Toma menos de 2 minutos enviar",
    pt: "Leva menos de 2 minutos para enviar",
  },
  // Lightweight 3-step indicator (visual only — single-flow form)
  "qf.steps.label": {
    en: "Step",
    es: "Paso",
    pt: "Passo",
  },
  "qf.steps.of": {
    en: "of",
    es: "de",
    pt: "de",
  },
  "qf.steps.1": {
    en: "Your request",
    es: "Tu solicitud",
    pt: "Seu pedido",
  },
  "qf.steps.2": {
    en: "Delivery",
    es: "Entrega",
    pt: "Entrega",
  },
  "qf.steps.3": {
    en: "Contact",
    es: "Contacto",
    pt: "Contato",
  },
  // Inline pre-submit feedback (sets expectations next to CTA)
  "qf.preSubmit.routed": {
    en: "Sent once — sourced across multiple U.S. suppliers and OEM-authorized distributors",
    es: "Enviado una vez — abastecido en múltiples proveedores de EE. UU. y distribuidores autorizados OEM",
    pt: "Enviado uma vez — sourcing em múltiplos fornecedores dos EUA e distribuidores autorizados OEM",
  },
  "qf.preSubmit.ddp": {
    en: "Quote includes full landed pricing (product, shipping, duties, delivery) and equivalent options where available",
    es: "La cotización incluye precio total puesto (producto, envío, aranceles, entrega) y equivalentes cuando aplique",
    pt: "A cotação inclui preço total entregue (produto, frete, impostos, entrega) e equivalentes quando disponíveis",
  },
  "qf.advanced.title": {
    en: "Advanced Details (optional)",
    es: "Detalles avanzados (opcional)",
    pt: "Detalhes avançados (opcional)",
  },
  "qf.advanced.subtitle": {
    en: "Application notes, sourcing preferences, additional requirements",
    es: "Notas de aplicación, preferencias de abastecimiento, requisitos adicionales",
    pt: "Notas de aplicação, preferências de sourcing, requisitos adicionais",
  },
  "qf.advanced.show": {
    en: "Show advanced details",
    es: "Mostrar detalles avanzados",
    pt: "Mostrar detalhes avançados",
  },
  "qf.advanced.hide": {
    en: "Hide advanced details",
    es: "Ocultar detalles avanzados",
    pt: "Ocultar detalhes avançados",
  },
  // Delivery & Contact collapsible drawer (homepage compact view)
  "qf.deliveryContact.title": {
    en: "Delivery & Contact details",
    es: "Detalles de entrega y contacto",
    pt: "Detalhes de entrega e contato",
  },
  "qf.deliveryContact.subtitle": {
    en: "Required — country, name, company, and email or WhatsApp.",
    es: "Requerido — país, nombre, empresa, y correo o WhatsApp.",
    pt: "Obrigatório — país, nome, empresa, e e-mail ou WhatsApp.",
  },
  "qf.deliveryContact.show": {
    en: "Show delivery & contact details",
    es: "Mostrar detalles de entrega y contacto",
    pt: "Mostrar detalhes de entrega e contato",
  },
  "qf.deliveryContact.hide": {
    en: "Hide delivery & contact details",
    es: "Ocultar detalles de entrega y contacto",
    pt: "Ocultar detalhes de entrega e contato",
  },
  "qf.deliveryContact.complete": {
    en: "Complete",
    es: "Completo",
    pt: "Completo",
  },
  "qf.deliveryContact.required": {
    en: "Required",
    es: "Requerido",
    pt: "Obrigatório",
  },
  "qf.deliveryContact.autoOpened": {
    en: "Last step — add your delivery and contact details to submit.",
    es: "Último paso — agrega tus datos de entrega y contacto para enviar.",
    pt: "Última etapa — adicione seus dados de entrega e contato para enviar.",
  },
  // Slim form — intro + microcopy
  "qf.intro": {
    en: "Need a component fast? Submit your request below.",
    es: "¿Necesitas un componente rápido? Envía tu solicitud abajo.",
    pt: "Precisa de um componente rápido? Envie sua solicitação abaixo.",
  },
  "qf.skuHint": {
    en: "If you don't have the exact part number, describe the component.",
    es: "Si no tienes el número de parte exacto, describe el componente.",
    pt: "Se você não tem o número exato da peça, descreva o componente.",
  },
  "qf.responseMicro": {
    en: "We typically respond within a few hours with availability and pricing.",
    es: "Normalmente respondemos en pocas horas con disponibilidad y precio.",
    pt: "Normalmente respondemos em poucas horas com disponibilidade e preço.",
  },
  "qf.systemLine": {
    en: "Review your quote and proceed to checkout directly — no back-and-forth required.",
    es: "Revisa tu cotización y procede al pago directamente — sin idas y vueltas.",
    pt: "Revise sua cotação e prossiga para o checkout diretamente — sem idas e voltas.",
  },
  "qf.notes.application": {
    en: "Application / Notes",
    es: "Aplicación / Notas",
    pt: "Aplicação / Notas",
  },
  "qf.notes.application.ph": {
    en: "Optional: replacement, project, maintenance, etc.",
    es: "Opcional: reemplazo, proyecto, mantenimiento, etc.",
    pt: "Opcional: substituição, projeto, manutenção, etc.",
  },
  "qf.quantity.helper": {
    en: "Approximate quantity is fine.",
    es: "Una cantidad aproximada está bien.",
    pt: "Uma quantidade aproximada já é suficiente.",
  },
  "qf.notes.application.helper": {
    en: "Add urgency, specs, or acceptable substitutes (optional).",
    es: "Añada urgencia, especificaciones o sustitutos aceptables (opcional).",
    pt: "Adicione urgência, especificações ou substitutos aceitáveis (opcional).",
  },
  "qf.brand.preferred": {
    en: "Preferred Brand (if any)",
    es: "Marca Preferida (si tiene)",
    pt: "Marca Preferida (se houver)",
  },
  "qf.submit.requestQuote": {
    en: "Request Quote",
    es: "Solicitar Cotización",
    pt: "Solicitar Cotação",
  },
  "qf.addMore": {
    en: "Add More Details",
    es: "Agregar Más Detalles",
    pt: "Adicionar Mais Detalhes",
  },
  "qf.group.contact": { en: "Contact & order", es: "Contacto y pedido", pt: "Contato e pedido" },
  "qf.group.product": { en: "Product", es: "Producto", pt: "Produto" },
  "qf.group.specs": { en: "Specifications", es: "Especificaciones", pt: "Especificações" },
  "qf.group.sourcing": { en: "Sourcing", es: "Abastecimiento", pt: "Sourcing" },
  "qf.group.delivery": { en: "Delivery", es: "Entrega", pt: "Entrega" },
  "qf.group.company": { en: "Company", es: "Empresa", pt: "Empresa" },
  "qf.group.other": { en: "Other", es: "Otro", pt: "Outro" },
  "qf.brand": { en: "Brand", es: "Marca", pt: "Marca" },
  "qf.productType": { en: "Product Type", es: "Tipo de Producto", pt: "Tipo de Produto" },
  "qf.productType.ph": {
    en: "e.g. Circuit breaker",
    es: "ej. Interruptor",
    pt: "ex. Disjuntor",
  },
  "qf.productLink": { en: "Product Link", es: "Enlace del Producto", pt: "Link do Produto" },
  "qf.upload": { en: "Upload file", es: "Subir archivo", pt: "Enviar arquivo" },
  "qf.upload.ph": {
    en: "Spec sheet, photo, or PDF",
    es: "Ficha técnica, foto o PDF",
    pt: "Ficha técnica, foto ou PDF",
  },
  "qf.browse": { en: "Browse", es: "Examinar", pt: "Procurar" },
  "qf.voltage": { en: "Voltage", es: "Voltaje", pt: "Tensão" },
  "qf.phase": { en: "Phase", es: "Fase", pt: "Fase" },
  "qf.phase.ph": { en: "Select phase", es: "Seleccione fase", pt: "Selecione fase" },
  "qf.phase.single": { en: "Single phase", es: "Monofásico", pt: "Monofásico" },
  "qf.phase.three": { en: "Three phase", es: "Trifásico", pt: "Trifásico" },
  "qf.phase.dc": { en: "DC", es: "CC", pt: "CC" },
  "qf.frequency": { en: "Frequency", es: "Frecuencia", pt: "Frequência" },
  "qf.amperage": { en: "Amperage", es: "Amperaje", pt: "Amperagem" },
  "qf.modelSeries": { en: "Model / Series", es: "Modelo / Serie", pt: "Modelo / Série" },
  "qf.exact": {
    en: "Exact Part or Equivalent",
    es: "Parte Exacta o Equivalente",
    pt: "Peça Exata ou Equivalente",
  },
  "qf.exact.ph": {
    en: "Select preference",
    es: "Seleccione preferencia",
    pt: "Selecione preferência",
  },
  "qf.exact.exact": {
    en: "Exact part only",
    es: "Solo parte exacta",
    pt: "Apenas peça exata",
  },
  "qf.exact.equivalent": {
    en: "Equivalent acceptable",
    es: "Equivalente aceptable",
    pt: "Equivalente aceitável",
  },
  "qf.exact.either": {
    en: "Either is fine",
    es: "Cualquiera está bien",
    pt: "Qualquer um serve",
  },
  "qf.fullAddress": { en: "Full Address", es: "Dirección Completa", pt: "Endereço Completo" },
  "qf.fullAddress.ph": {
    en: "Street, number, building, references…",
    es: "Calle, número, edificio, referencias…",
    pt: "Rua, número, edifício, referências…",
  },
  "qf.companyType": { en: "Company Type", es: "Tipo de Empresa", pt: "Tipo de Empresa" },
  "qf.companyType.ph": {
    en: "Select company type",
    es: "Seleccione tipo de empresa",
    pt: "Selecione o tipo de empresa",
  },
  "qf.companyType.contractor": { en: "Contractor", es: "Contratista", pt: "Empreiteira" },
  "qf.companyType.epc": { en: "EPC", es: "EPC", pt: "EPC" },
  "qf.companyType.industrial": {
    en: "Industrial / Plant",
    es: "Industrial / Planta",
    pt: "Industrial / Planta",
  },
  "qf.companyType.distributor": { en: "Distributor", es: "Distribuidor", pt: "Distribuidor" },
  "qf.companyType.integrator": { en: "Integrator", es: "Integrador", pt: "Integrador" },
  "qf.companyType.other": { en: "Other", es: "Otro", pt: "Outro" },
  "qf.sku": { en: "SKU / Part Number", es: "SKU / Número de Parte", pt: "SKU / Código da Peça" },
  "qf.sku.ph": {
    en: "e.g. Siemens 3VA1110 or product URL",
    es: "ej. Siemens 3VA1110 o URL del producto",
    pt: "ex. Siemens 3VA1110 ou URL do produto",
  },
  "qf.product.ph.optional": {
    en: "Optional — describe the part if you don't have an SKU",
    es: "Opcional — describa la parte si no tiene SKU",
    pt: "Opcional — descreva a peça se não tiver SKU",
  },
  "qf.hasSpecs": {
    en: "Do you have full specifications?",
    es: "¿Tiene las especificaciones completas?",
    pt: "Você tem as especificações completas?",
  },
  "qf.hasSpecs.ph": { en: "Select", es: "Seleccione", pt: "Selecione" },
  "qf.hasSpecs.yes": { en: "Yes", es: "Sí", pt: "Sim" },
  "qf.hasSpecs.no": {
    en: "No — please confirm with us",
    es: "No — por favor confírmenlo con nosotros",
    pt: "Não — por favor confirmem conosco",
  },
  "qf.deliveryType": {
    en: "Delivery Type",
    es: "Tipo de Entrega",
    pt: "Tipo de Entrega",
  },
  "qf.deliveryType.ph": {
    en: "Select delivery type",
    es: "Seleccione tipo de entrega",
    pt: "Selecione o tipo de entrega",
  },
  "qf.deliveryType.business": {
    en: "Business / Commercial",
    es: "Empresa / Comercial",
    pt: "Empresa / Comercial",
  },
  "qf.deliveryType.residential": {
    en: "Residential",
    es: "Residencial",
    pt: "Residencial",
  },
  "qf.orderType": {
    en: "Is this for…",
    es: "¿Esto es para…",
    pt: "Isto é para…",
  },
  "qf.orderType.ph": { en: "Optional", es: "Opcional", pt: "Opcional" },
  "qf.orderType.oneTime": {
    en: "One-time order",
    es: "Pedido único",
    pt: "Pedido único",
  },
  "qf.orderType.ongoing": {
    en: "Ongoing / repeat needs",
    es: "Necesidades recurrentes",
    pt: "Necessidades recorrentes",
  },
  "qf.group.commercial": {
    en: "Commercial",
    es: "Comercial",
    pt: "Comercial",
  },
  "qf.notes": {
    en: "Specs, quantity, or requirements",
    es: "Especificaciones, cantidad o requisitos",
    pt: "Especificações, quantidade ou requisitos",
  },
  "qf.notes.ph": {
    en: "e.g. 24 units, 480V, delivery location",
    es: "ej. 24 unidades, 480V, lugar de entrega",
    pt: "ex. 24 unidades, 480V, local de entrega",
  },
  "qf.upload.label": {
    en: "Upload spec sheet / BOM (optional)",
    es: "Subir hoja técnica / BOM (opcional)",
    pt: "Enviar ficha técnica / BOM (opcional)",
  },
  "qf.upload.cta": {
    en: "Choose file",
    es: "Elegir archivo",
    pt: "Escolher arquivo",
  },
  "qf.upload.hint": {
    en: "Upload drawings, specs, or BOM for faster and more accurate quoting. PDF, DOC, XLS, PNG, JPG · 20MB max.",
    es: "Sube planos, especificaciones o BOM para una cotización más rápida y precisa. PDF, DOC, XLS, PNG, JPG · 20MB máx.",
    pt: "Envie desenhos, especificações ou BOM para uma cotação mais rápida e precisa. PDF, DOC, XLS, PNG, JPG · 20MB máx.",
  },
  "qf.upload.remove": {
    en: "Remove",
    es: "Quitar",
    pt: "Remover",
  },
  "qf.upload.uploading": {
    en: "Uploading…",
    es: "Subiendo…",
    pt: "Enviando…",
  },
  "qf.upload.error.size": {
    en: "File is too large. Max 20MB.",
    es: "El archivo es demasiado grande. Máx. 20MB.",
    pt: "Arquivo muito grande. Máx. 20MB.",
  },
  "qf.upload.error.type": {
    en: "Unsupported file type. Use PDF, DOC, XLS, PNG, or JPG.",
    es: "Tipo de archivo no compatible. Usa PDF, DOC, XLS, PNG o JPG.",
    pt: "Tipo de arquivo não suportado. Use PDF, DOC, XLS, PNG ou JPG.",
  },
  "qf.upload.error.failed": {
    en: "Upload failed. Please try again.",
    es: "Error al subir. Inténtalo de nuevo.",
    pt: "Falha no envio. Tente novamente.",
  },
  "qf.submit": {
    en: "Submit Parts List",
    es: "Enviar Lista de Partes",
    pt: "Enviar Lista de Peças",
  },
  "qf.submitting": { en: "Sending…", es: "Enviando…", pt: "Enviando…" },
  "qf.footer": {
    en: "We confirm availability and respond with pricing and delivery timeline — typically within hours.",
    es: "Confirmamos disponibilidad y respondemos con precio y plazo de entrega — típicamente en pocas horas.",
    pt: "Confirmamos disponibilidade e respondemos com preço e prazo de entrega — normalmente em poucas horas.",
  },
  "qf.toast.title": {
    en: "Request received",
    es: "Solicitud recibida",
    pt: "Solicitação recebida",
  },
  "qf.toast.desc": {
    en: "Processing your request — matching products and suppliers now.",
    es: "Procesando tu solicitud — emparejando productos y proveedores ahora.",
    pt: "Processando sua solicitação — combinando produtos e fornecedores agora.",
  },
  "qf.error.title": {
    en: "Submission failed",
    es: "Error al enviar la solicitud",
    pt: "Falha ao enviar a solicitação",
  },
  "qf.error.desc": {
    en: "There was an issue submitting your request. Please contact us via WhatsApp.",
    es: "Hubo un problema al enviar su solicitud. Por favor contáctenos por WhatsApp.",
    pt: "Houve um problema ao enviar sua solicitação. Entre em contato pelo WhatsApp.",
  },
  "qf.invalid.title": {
    en: "Please fix the highlighted fields",
    es: "Por favor corrija los campos resaltados",
    pt: "Por favor corrija os campos destacados",
  },
  "qf.invalid.desc": {
    en: "We've scrolled to the first issue — required fields are marked in red.",
    es: "Le hemos llevado al primer problema — los campos obligatorios están marcados en rojo.",
    pt: "Levamos você até o primeiro problema — os campos obrigatórios estão marcados em vermelho.",
  },
  "qf.invalid.inline": {
    en: "Please correct the highlighted fields and try again.",
    es: "Por favor corrige los campos resaltados e intenta de nuevo.",
    pt: "Por favor corrija os campos destacados e tente novamente.",
  },
  "qf.invalid.specs": {
    en: "Without an SKU, please add at least one specification or upload a photo / spec sheet.",
    es: "Sin un SKU, agrega al menos una especificación o sube una foto / hoja técnica.",
    pt: "Sem um SKU, adicione pelo menos uma especificação ou envie uma foto / ficha técnica.",
  },

  // ───────── WhatsAppCTA ─────────
  "wa.chip": { en: "Fastest channel", es: "Canal más rápido", pt: "Canal mais rápido" },
  "wa.h2": {
    en: "Prefer WhatsApp?",
    es: "¿Prefiere WhatsApp?",
    pt: "Prefere WhatsApp?",
  },
  "wa.text": {
    en: "Send details — we respond with structured pricing, availability, and delivery options.",
    es: "Envía los detalles — respondemos con precio, disponibilidad y opciones de entrega estructuradas.",
    pt: "Envie os detalhes — respondemos com preço, disponibilidade e opções de entrega estruturadas.",
  },
  "wa.footer": {
    en: "Typically responsive during business hours.",
    es: "Normalmente disponibles en horario laboral.",
    pt: "Normalmente disponíveis em horário comercial.",
  },

  // ───────── FinalCTA ─────────
  "final.h2": {
    en: "Need a component fast?",
    es: "¿Necesitas un componente rápido?",
    pt: "Precisa de um componente rápido?",
  },
  "final.text": {
    en: "Submit your request and receive availability and full landed (DDP) pricing in under 4 hours.",
    es: "Envía tu solicitud y recibe disponibilidad y precio total (DDP) en menos de 4 horas.",
    pt: "Envie seu pedido e receba disponibilidade e preço total (DDP) em menos de 4 horas.",
  },
  "final.cta.primary": {
    en: "Get Pricing & Availability",
    es: "Obtener Precio y Disponibilidad",
    pt: "Obter Preço e Disponibilidade",
  },
  "final.cta.primary.micro": {
    en: "Response in < 4 hours",
    es: "Respuesta en < 4 horas",
    pt: "Resposta em < 4 horas",
  },
  "final.cta.whatsapp.micro": {
    en: "For urgent requests",
    es: "Para solicitudes urgentes",
    pt: "Para pedidos urgentes",
  },
  "final.trust": {
    en: "Verified U.S. suppliers • 5–10 day DDP delivery to LATAM • Full landed pricing — no hidden fees",
    es: "Proveedores verificados de EE. UU. • Entrega DDP en 5–10 días a LATAM • Precio total puesto — sin cargos ocultos",
    pt: "Fornecedores verificados dos EUA • Entrega DDP em 5–10 dias para LATAM • Preço total entregue — sem taxas ocultas",
  },

  // ───────── Footer ─────────
  "footer.tagline": {
    en: "Powering Infrastructure. Enabling Progress.",
    es: "Impulsando la infraestructura. Habilitando el progreso.",
    pt: "Impulsionando a infraestrutura. Viabilizando o progresso.",
  },
  "footer.contact": { en: "Contact", es: "Contacto", pt: "Contato" },
  "footer.supplierPartners": {
    en: "Become a supplier",
    es: "Ser proveedor",
    pt: "Seja fornecedor",
  },
  "footer.legal": { en: "Legal", es: "Legal", pt: "Legal" },
  "footer.explore": { en: "Explore", es: "Explorar", pt: "Explorar" },
  "footer.solutions": { en: "Solutions", es: "Soluciones", pt: "Soluções" },
  "footer.compare": { en: "Compare", es: "Comparar", pt: "Comparar" },
 "footer.buyerLogin": { en: "Sign in", es: "Iniciar sesión", pt: "Entrar" },
 "footer.createAccount": { en: "Create account", es: "Crear cuenta", pt: "Criar conta" },
 "footer.myAccount": { en: "My account", es: "Mi cuenta", pt: "Minha conta" },
 "footer.logout": { en: "Logout", es: "Cerrar sesión", pt: "Sair" },
 "footer.supplierLogin": { en: "Supplier login", es: "Acceso Proveedores", pt: "Login Fornecedor" },
  "footer.privacy": {
    en: "Privacy Policy",
    es: "Política de Privacidad",
    pt: "Política de Privacidade",
  },
  "footer.terms": {
    en: "Terms",
    es: "Términos",
    pt: "Termos",
  },
  "footer.copy": {
    en: "All rights reserved.",
    es: "Todos los derechos reservados.",
    pt: "Todos os direitos reservados.",
  },
  "footer.tagline2": {
    en: "The transaction layer for industrial supply.",
    es: "La capa de transacción para el suministro industrial.",
    pt: "A camada de transação para o suprimento industrial.",
  },

  // ───────── Newsletter (footer) ─────────
  "newsletter.title": {
    en: "Stay Updated",
    es: "Mantente Informado",
    pt: "Fique Atualizado",
  },
  "newsletter.subtext": {
    en: "Get availability updates and hard-to-find electrical components from U.S. suppliers.",
    es: "Recibe actualizaciones de disponibilidad y componentes eléctricos difíciles de encontrar de proveedores de EE. UU.",
    pt: "Receba atualizações de disponibilidade e componentes elétricos difíceis de encontrar de fornecedores dos EUA.",
  },
  "newsletter.placeholder": {
    en: "Enter your email",
    es: "Ingresa tu correo",
    pt: "Insira seu e-mail",
  },
  "newsletter.cta": { en: "Subscribe", es: "Suscribirse", pt: "Inscrever-se" },
  "newsletter.submitting": {
    en: "Subscribing…",
    es: "Suscribiendo…",
    pt: "Inscrevendo…",
  },
  "newsletter.micro": {
    en: "No spam. Only relevant updates.",
    es: "Sin spam. Solo actualizaciones relevantes.",
    pt: "Sem spam. Apenas atualizações relevantes.",
  },
  "newsletter.success": {
    en: "You're subscribed.",
    es: "Estás suscrito.",
    pt: "Você está inscrito.",
  },
  "newsletter.invalid": {
    en: "Please enter a valid email address.",
    es: "Por favor ingresa un correo válido.",
    pt: "Por favor insira um e-mail válido.",
  },
  "newsletter.error": {
    en: "Something went wrong. Please try again.",
    es: "Algo salió mal. Intenta de nuevo.",
    pt: "Algo deu errado. Tente novamente.",
  },

  // ───────── Floating WhatsApp aria ─────────
  "fw.aria": {
    en: "Chat on WhatsApp",
    es: "Chatear por WhatsApp",
    pt: "Conversar no WhatsApp",
  },

  // ───────── Language banner ─────────
  "banner.es": {
    en: "View this site in Spanish?",
    es: "¿Ver este sitio en Español?",
    pt: "Ver este site em Espanhol?",
  },
  "banner.pt": {
    en: "View this site in Portuguese?",
    es: "¿Ver este sitio en Portugués?",
    pt: "Ver este site em Português?",
  },
  "banner.switch": { en: "Switch", es: "Cambiar", pt: "Mudar" },
  "banner.dismiss": { en: "Dismiss", es: "Descartar", pt: "Dispensar" },

  // ───────── Legal common ─────────
  "legal.back": {
    en: "Back to home",
    es: "Volver al inicio",
    pt: "Voltar ao início",
  },
  "legal.eyebrow": { en: "Legal", es: "Legal", pt: "Legal" },
  "legal.lastUpdated": {
    en: "Last updated:",
    es: "Última actualización:",
    pt: "Última atualização:",
  },

  // ───────── SEO meta ─────────
  "seo.home.title": {
    en: "U.S. Switchgear & Electrical Components for LATAM | Switzh",
    es: "Switchgear y Componentes Eléctricos de EE. UU. para LATAM | Switzh",
    pt: "Switchgear e Componentes Elétricos dos EUA para LATAM | Switzh",
  },
  "seo.home.desc": {
    en: "Fast-access procurement of U.S. switchgear, breakers, and electrical components for LATAM. Firm quotes in under 4 hours, full landed (DDP) pricing, tracked delivery.",
    es: "Compra rápida de switchgear, interruptores y componentes eléctricos de EE. UU. para LATAM. Cotizaciones firmes en menos de 4 horas, precio total entregado (DDP) y entrega rastreada.",
    pt: "Compra rápida de switchgear, disjuntores e componentes elétricos dos EUA para a LATAM. Cotações firmes em menos de 4 horas, preço total entregue (DDP) e entrega rastreada.",
  },
  "seo.privacy.title": {
    en: "Privacy Policy | Switzh",
    es: "Política de Privacidad | Switzh",
    pt: "Política de Privacidade | Switzh",
  },
  "seo.privacy.desc": {
    en: "How Switzh collects, uses, and protects buyer data during industrial electrical sourcing and quoting.",
    es: "Cómo Switzh recopila, usa y protege los datos del comprador durante el sourcing y cotización industrial.",
    pt: "Como a Switzh coleta, usa e protege dados do comprador durante o sourcing e cotação industrial.",
  },
  "seo.terms.title": {
    en: "Terms of Service | Switzh",
    es: "Términos de Servicio | Switzh",
    pt: "Termos de Serviço | Switzh",
  },
  "seo.terms.desc": {
    en: "Commercial terms for quoting, ordering, and delivery of industrial electrical components through Switzh.",
    es: "Términos comerciales para cotizar, pedir y entregar componentes eléctricos industriales a través de Switzh.",
    pt: "Termos comerciais para cotar, pedir e entregar componentes elétricos industriais via Switzh.",
  },
  "seo.compare.title": {
    en: "Electrical Sourcing vs Distributors (Speed Comparison)",
    es: "Sourcing Eléctrico vs Distribuidores (Velocidad)",
    pt: "Sourcing Elétrico vs Distribuidores (Velocidade)",
  },
  "seo.compare.desc": {
    en: "Compare lead times, pricing, and supplier coverage vs traditional distributors. Quotes in hours, multi-supplier sourcing, DDP delivery to LATAM.",
    es: "Compara plazos, precios y cobertura vs distribuidores tradicionales. Cotizaciones en horas, multi-proveedor, entrega DDP en LATAM.",
    pt: "Compare prazos, preços e cobertura vs distribuidores tradicionais. Cotações em horas, multi-fornecedor, entrega DDP na LATAM.",
  },
  "seo.requestQuote.title": {
    en: "Request a Quote — Industrial Electrical Components",
    es: "Solicitar Cotización — Componentes Eléctricos",
    pt: "Solicitar Cotação — Componentes Elétricos",
  },
  "seo.requestQuote.desc": {
    en: "Send a SKU, spec sheet, or photo and get firm pricing plus availability within hours. Global supplier network, no account required.",
    es: "Envía SKU, ficha técnica o foto y recibe precio firme y disponibilidad en horas. Red global de proveedores, sin necesidad de cuenta.",
    pt: "Envie SKU, ficha técnica ou foto e receba preço firme e disponibilidade em horas. Rede global de fornecedores, sem cadastro.",
  },
  "seo.howItWorks.title": {
    en: "Electrical Procurement Services — From SKU to Delivery",
    es: "Servicios de Procura Eléctrica — Del SKU a la Entrega",
    pt: "Serviços de Procura Elétrica — Do SKU à Entrega",
  },
  "seo.howItWorks.desc": {
    en: "How we source hard-to-find industrial electrical components, return firm quotes in hours, and deliver DDP to LATAM. Step-by-step process.",
    es: "Cómo abastecemos componentes eléctricos difíciles de encontrar, cotizamos en horas y entregamos DDP en LATAM. Proceso paso a paso.",
    pt: "Como abastecemos componentes elétricos difíceis de encontrar, cotamos em horas e entregamos DDP na LATAM. Processo passo a passo.",
  },
  "seo.whatWeSource.title": {
    en: "What We Source — Brands, SKUs & Component Categories",
    es: "Qué Suministramos — Marcas, SKUs y Categorías",
    pt: "O Que Fornecemos — Marcas, SKUs e Categorias",
  },
  "seo.whatWeSource.desc": {
    en: "Breakers, PLCs, VFDs, contactors, switchgear and sensors from ABB, Siemens, Schneider, Rockwell, Eaton — sourced fast through global suppliers.",
    es: "Interruptores, PLCs, VFDs, contactores, switchgear y sensores de ABB, Siemens, Schneider, Rockwell, Eaton — desde red global.",
    pt: "Disjuntores, PLCs, VFDs, contatores, switchgear e sensores de ABB, Siemens, Schneider, Rockwell, Eaton — via rede global.",
  },

  // ───────── Cookie Consent (GDPR / LGPD) ─────────
  "cookie.aria": {
    en: "Cookie consent",
    es: "Consentimiento de cookies",
    pt: "Consentimento de cookies",
  },
  "cookie.title": {
    en: "We value your privacy",
    es: "Valoramos tu privacidad",
    pt: "Valorizamos sua privacidade",
  },
  "cookie.text": {
    en: "We use cookies to analyze site traffic and improve your experience. You can accept or reject analytics cookies. See our",
    es: "Usamos cookies para analizar el tráfico del sitio y mejorar tu experiencia. Puedes aceptar o rechazar las cookies de análisis. Consulta nuestra",
    pt: "Usamos cookies para analisar o tráfego do site e melhorar sua experiência. Você pode aceitar ou recusar cookies de análise. Veja nossa",
  },
  "cookie.privacy": {
    en: "Privacy Policy",
    es: "Política de Privacidad",
    pt: "Política de Privacidade",
  },
  "cookie.accept": {
    en: "Accept all",
    es: "Aceptar todo",
    pt: "Aceitar tudo",
  },
  "cookie.reject": {
    en: "Reject",
    es: "Rechazar",
    pt: "Recusar",
  },
  "cookie.close": {
    en: "Close",
    es: "Cerrar",
    pt: "Fechar",
  },
  "footer.cookiePrefs": {
    en: "Cookie preferences",
    es: "Preferencias de cookies",
    pt: "Preferências de cookies",
  },

  // ───────── 3-Layer Positioning (Procurement Paths) ─────────
  "layers.title": {
    en: "What Switzh Does",
    es: "Qué Hace Switzh",
    pt: "O Que a Switzh Faz",
  },
  "layers.intro": {
    en: "A fast-access procurement and pricing layer for LATAM buyers sourcing U.S. electrical supply. Components first — packages and made-to-order systems when you need them.",
    es: "Una capa de compras y precios de acceso rápido para compradores LATAM que adquieren suministro eléctrico de EE. UU. Componentes primero — paquetes y sistemas a medida cuando los necesites.",
    pt: "Uma camada de compras e preços de acesso rápido para compradores LATAM que adquirem suprimento elétrico dos EUA. Componentes primeiro — pacotes e sistemas sob medida quando precisar.",
  },

  "layers.core.eyebrow": { en: "Primary lane", es: "Vía principal", pt: "Via principal" },
  "layers.core.title": {
    en: "Fast Access Power Components",
    es: "Componentes Eléctricos de Acceso Rápido",
    pt: "Componentes Elétricos de Acesso Rápido",
  },
  "layers.core.body": {
    en: "Our core business: urgent, repeat, and hard-to-source U.S. components — switchgear, breakers, contactors, relays, protection devices. Quotes targeted in under 4 hours.",
    es: "Nuestro negocio principal: componentes de EE. UU. urgentes, recurrentes y difíciles de encontrar — switchgear, interruptores, contactores, relés y dispositivos de protección. Cotizaciones objetivo en menos de 4 horas.",
    pt: "Nosso negócio principal: componentes dos EUA urgentes, recorrentes e difíceis de encontrar — switchgear, disjuntores, contatores, relés e dispositivos de proteção. Cotações em menos de 4 horas.",
  },

  "layers.fast.eyebrow": { en: "Bundled procurement", es: "Compras agrupadas", pt: "Compras agrupadas" },
  "layers.fast.title": {
    en: "Pre-Configured Power Packages",
    es: "Paquetes de Energía Pre-Configurados",
    pt: "Pacotes de Energia Pré-Configurados",
  },
  "layers.fast.body": {
    en: "Standardized bundles for repeatable use cases — faster quoting, simpler procurement, larger basket size in a single order.",
    es: "Paquetes estandarizados para casos repetibles — cotización más rápida, compras más simples y mayor volumen en un solo pedido.",
    pt: "Pacotes padronizados para casos repetíveis — cotação mais rápida, compras mais simples e maior volume em um único pedido.",
  },
  "layers.fast.items": {
    en: "Common contractor & maintenance bundles · pre-spec'd kits",
    es: "Paquetes comunes para contratistas y mantenimiento · kits pre-especificados",
    pt: "Pacotes comuns para contratantes e manutenção · kits pré-especificados",
  },
  "layers.fast.speed": {
    en: "Bundled • Faster quoting • Higher AOV",
    es: "Agrupado • Cotización más rápida • Mayor valor por pedido",
    pt: "Agrupado • Cotação mais rápida • Maior valor por pedido",
  },

  "layers.eng.eyebrow": { en: "Made-to-order", es: "A medida", pt: "Sob medida" },
  "layers.eng.title": {
    en: "Power Distribution Solutions",
    es: "Soluciones de Distribución Eléctrica",
    pt: "Soluções de Distribuição Elétrica",
  },
  "layers.eng.body": {
    en: "Made-to-order systems for planned projects with defined scope and longer lead times — not the primary lane for urgent component requests.",
    es: "Sistemas a medida para proyectos planificados con alcance definido y plazos más largos — no es la vía principal para solicitudes urgentes de componentes.",
    pt: "Sistemas sob medida para projetos planejados com escopo definido e prazos mais longos — não é a via principal para solicitações urgentes de componentes.",
  },
  "layers.eng.items": {
    en: "Switchgear · ATS / STS · Panels · Power distribution systems",
    es: "Switchgear · ATS / STS · Paneles · Sistemas de distribución",
    pt: "Switchgear · ATS / STS · Painéis · Sistemas de distribuição",
  },
  "layers.eng.scope": {
    en: "Made-to-order • Defined scope • Longer cycle",
    es: "A medida • Alcance definido • Ciclo más largo",
    pt: "Sob medida • Escopo definido • Ciclo mais longo",
  },

  // ───────── Product/System Labels ─────────
  "label.fastAccess": {
    en: "Fast Access Solution — optimized for urgent sourcing",
    es: "Solución de Acceso Rápido — optimizada para sourcing urgente",
    pt: "Solução de Acesso Rápido — otimizada para sourcing urgente",
  },
  "label.engineered": {
    en: "Power Distribution Solution — built-to-order with extended lead times",
    es: "Solución de Distribución Eléctrica — fabricada sobre pedido con plazos extendidos",
    pt: "Solução de Distribuição Elétrica — fabricada sob encomenda com prazos estendidos",
  },
  "label.leadFast": {
    en: "Typical sourcing time: 24–72 hours",
    es: "Tiempo típico de sourcing: 24–72 horas",
    pt: "Tempo típico de sourcing: 24–72 horas",
  },
  "label.leadEngineered": {
    en: "Typical lead time: 2–8+ weeks depending on configuration",
    es: "Plazo típico: 2–8+ semanas según configuración",
    pt: "Prazo típico: 2–8+ semanas conforme configuração",
  },

  // ───────── Trust line (global) ─────────
  "trust.globalNetwork": {
    en: "We source through a global network of suppliers — not limited to a single distributor or manufacturer.",
    es: "Abastecemos a través de una red global de proveedores — no estamos limitados a un solo distribuidor o fabricante.",
    pt: "Fornecemos através de uma rede global de fornecedores — não estamos limitados a um único distribuidor ou fabricante.",
  },

  // ───────── WhatWeSource — How We Support Your Procurement ─────────
  "wws.twoWays.title": {
    en: "How We Support Your Procurement",
    es: "Cómo Apoyamos tu Procura",
    pt: "Como Apoiamos sua Procura",
  },
  "wws.twoWays.sub": {
    en: "Three procurement layers — pick the path that matches your situation.",
    es: "Tres capas de procura — elige la ruta que coincida con tu situación.",
    pt: "Três camadas de procura — escolha o caminho que combina com sua situação.",
  },

  // ───────── Decision Path ─────────
  "decision.title": {
    en: "What do you need?",
    es: "¿Qué necesitas?",
    pt: "Do que você precisa?",
  },
  "decision.fast.title": {
    en: "Fast Access Power Components",
    es: "Componentes de Acceso Rápido",
    pt: "Componentes de Acesso Rápido",
  },
  "decision.fast.label": {
    en: "Sourced in 24–72h",
    es: "Abastecido en 24–72h",
    pt: "Fornecido em 24–72h",
  },
  "decision.fast.cta": { en: "View Components", es: "Ver Componentes", pt: "Ver Componentes" },
  "decision.kits.title": {
    en: "Pre-Configured Power Packages",
    es: "Paquetes de Energía Pre-Configurados",
    pt: "Pacotes de Energia Pré-Configurados",
  },
  "decision.kits.label": {
    en: "Ready-to-install, faster deployment",
    es: "Listos para instalar, despliegue más rápido",
    pt: "Prontos para instalar, implantação mais rápida",
  },
  "decision.kits.cta": { en: "View Kits", es: "Ver Kits", pt: "Ver Kits" },
  "decision.eng.title": {
    en: "Power Distribution Solutions",
    es: "Soluciones de Distribución Eléctrica",
    pt: "Soluções de Distribuição Elétrica",
  },
  "decision.eng.label": {
    en: "Built-to-order (2–8+ weeks)",
    es: "Fabricado sobre pedido (2–8+ semanas)",
    pt: "Fabricado sob encomenda (2–8+ semanas)",
  },
  "decision.eng.cta": { en: "View Systems", es: "Ver Sistemas", pt: "Ver Sistemas" },

  // ───────── Recently Sourced ─────────
  "recent.eyebrow": {
    en: "Recently sourced",
    es: "Recientemente abastecido",
    pt: "Recentemente fornecido",
  },
  "recent.suffix.days": {
    en: "days",
    es: "días",
    pt: "dias",
  },

  // ───────── Most Requested ─────────
  "mostReq.title": {
    en: "Most Requested Power Packages",
    es: "Paquetes de Energía Más Solicitados",
    pt: "Pacotes de Energia Mais Solicitados",
  },
  "mostReq.sub": {
    en: "Pre-configured component bundles sourced from U.S. supply and delivered into LATAM with full landed pricing (DDP).",
    es: "Paquetes de componentes pre-configurados desde EE. UU., entregados en LATAM con precio total DDP.",
    pt: "Pacotes de componentes pré-configurados dos EUA, entregues na LATAM com preço total DDP.",
  },
  "mostReq.emergency.title": {
    en: "Emergency Replacement Kit",
    es: "Kit de Reemplazo de Emergencia",
    pt: "Kit de Substituição de Emergência",
  },
  "mostReq.emergency.body": {
    en: "Pre-configured kit for downtime scenarios — breaker, contactor, overload relay, and accessories sourced together for fastest LATAM delivery.",
    es: "Kit pre-configurado para escenarios de paro — interruptor, contactor, relé de sobrecarga y accesorios para entrega rápida en LATAM.",
    pt: "Kit pré-configurado para cenários de parada — disjuntor, contator, relé de sobrecarga e acessórios para entrega rápida na LATAM.",
  },
  "mostReq.panel.title": {
    en: "Panel Upgrade Kit",
    es: "Kit de Actualización de Panel",
    pt: "Kit de Atualização de Painel",
  },
  "mostReq.panel.body": {
    en: "Bundled main + branch breakers, busbars, and connectors for U.S.-standard panel upgrades, delivered DDP into LATAM.",
    es: "Interruptores principal y derivados, barras y conectores para actualizaciones de panel en estándar EE. UU., entregados DDP en LATAM.",
    pt: "Disjuntores principal e ramais, barramentos e conectores para atualizações de painel padrão EUA, entregues DDP na LATAM.",
  },
  "mostReq.automation.title": {
    en: "Automation Control Kit",
    es: "Kit de Control de Automatización",
    pt: "Kit de Controle de Automação",
  },
  "mostReq.automation.body": {
    en: "Contactor + overload + protection bundle from Schneider TeSys, Siemens 3RT, ABB AF — pre-validated for control system replacement.",
    es: "Paquete de contactor + sobrecarga + protección de Schneider TeSys, Siemens 3RT, ABB AF — pre-validado para reemplazo en sistemas de control.",
    pt: "Pacote de contator + sobrecarga + proteção de Schneider TeSys, Siemens 3RT, ABB AF — pré-validado para substituição em sistemas de controle.",
  },
  "mostReq.cta": {
    en: "Request Package Quote",
    es: "Solicitar Cotización del Paquete",
    pt: "Solicitar Cotação do Pacote",
  },
  "mostReq.badge": {
    en: "Primary",
    es: "Principal",
    pt: "Principal",
  },

  // ───────── Engineered Built-to-Order Block ─────────
  "bto.title": {
    en: "Built-to-Order Systems",
    es: "Sistemas Fabricados Sobre Pedido",
    pt: "Sistemas Fabricados Sob Encomenda",
  },
  "bto.body": {
    en: "These systems are configured based on project requirements and sourced from global suppliers. Lead times vary depending on specifications and availability.",
    es: "Estos sistemas se configuran según los requisitos del proyecto y se abastecen de proveedores globales. Los plazos varían según especificaciones y disponibilidad.",
    pt: "Estes sistemas são configurados conforme os requisitos do projeto e fornecidos por fornecedores globais. Os prazos variam conforme especificações e disponibilidade.",
  },
  "bto.leadLabel": {
    en: "Typical Lead Times",
    es: "Plazos Típicos",
    pt: "Prazos Típicos",
  },
  "bto.leadValue": {
    en: "2–8+ weeks",
    es: "2–8+ semanas",
    pt: "2–8+ semanas",
  },

  // ───────── Quote Response Expectation ─────────
  "quoteResp.title": {
    en: "Response expectation",
    es: "Tiempo de respuesta",
    pt: "Tempo de resposta",
  },
  "quoteResp.high.label": {
    en: "High Priority Requests",
    es: "Solicitudes de Alta Prioridad",
    pt: "Solicitações de Alta Prioridade",
  },
  "quoteResp.high.value": {
    en: "Response within 4 hours",
    es: "Respuesta en menos de 4 horas",
    pt: "Resposta em até 4 horas",
  },
  "quoteResp.std.label": {
    en: "Standard Requests",
    es: "Solicitudes Estándar",
    pt: "Solicitações Padrão",
  },
  "quoteResp.std.value": {
    en: "Response within 24 hours",
    es: "Respuesta en menos de 24 horas",
    pt: "Resposta em até 24 horas",
  },
} as const satisfies Record<string, Record<Lang, string>>;

export type TKey = keyof typeof TRANSLATIONS;
